import { Component, OnInit, AfterViewInit, OnDestroy, Renderer2 } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { StripePaymentService, CardPayment } from 'src/app/shared/services/stripe-payment.service';
import { HttpClient } from '@angular/common/http';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Subscription, interval, of } from 'rxjs';
import { retry, catchError, finalize, take, delay } from 'rxjs/operators';
import { AuthService } from 'src/app/shared/auth/auth.service';
import { environment } from 'src/environments/environment';
import { Utilities } from 'src/app/shared/util/utilities';

@Component({
  selector: 'app-card-payment-success',
  templateUrl: './card-payment-success.component.html',
  styleUrls: ['./card-payment-success.component.scss']
})
export class CardPaymentSuccessComponent implements OnInit, AfterViewInit, OnDestroy {
  sessionId: string | null = null;
  isFreePurchase: boolean = false;
  showSuccessMessage = true;
  documentReady = false;
  documentUrl: string | null = null;
  checkingDocument = false;
  checkAttempts = 0;
  maxCheckAttempts = 10;
  checkInterval = 3000; // 3 seconds between checks
  private docCheckSubscription: Subscription | null = null;
  isLoadingInfo = false;
  isDownloading = false;
  downloadError: string | null = null;
  documentStatus: string = 'pending';
  payment: any = null;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private stripeService: StripePaymentService,
    private http: HttpClient,
    private snackBar: MatSnackBar,
    private authService: AuthService,
    private renderer: Renderer2
  ) { }

  ngOnDestroy(): void {
    // Cancel all active subscriptions when leaving the component
    if (this.docCheckSubscription) {
      this.docCheckSubscription.unsubscribe();
    }
  }

  ngOnInit(): void {
    // Show an initial information message
    this.snackBar.open('Se descarcă documentul...', 'OK', { duration: 3000 });

    //Get session ID from both query params and URL segments
    this.route.params.subscribe(params => {
      const pathSessionId = params['sessionId'];
      if (pathSessionId) {
        this.handleSessionId(pathSessionId);
      }
    });

    this.route.queryParams.subscribe(params => {
      const querySessionId = params['sessionId'];
      if (querySessionId) {
        this.handleSessionId(querySessionId);
      }
    });
  }

  ngAfterViewInit(): void {
    // Intentionally left empty - initialization is handled in ngOnInit
  }

  goToDocuments(): void {
    console.log('Navigating to documents, current route:', this.router.url);
    this.router.navigate(['/documents']).then(
      success => console.log('Navigation successful'),
      error => {
        console.error('Navigation failed', error);
        this.snackBar.open('Nu s-a putut naviga la documente', 'OK', { duration: 3000 });
      }
    );
  }

  onDownloadDocument(): void {
    if (!this.sessionId) {
      this.snackBar.open('Nu există un ID de sesiune pentru descărcare.', 'OK', { duration: 3000 });
      return;
    }

    this.isDownloading = true;
    this.downloadError = null;

    const fullDocumentUrl = `${environment.backend}/api/cardpayments/public/session/${this.sessionId}/document`;

    fetch(fullDocumentUrl)
      .then(response => {
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}, statusText: ${response.statusText}`);
        }
        return response.json();
      })
      .then(jsonResponse => {
        if (jsonResponse['message'] === 'OK') {
          this.downloadFile(
            Utilities.dataURItoBlob(jsonResponse['content']),
            jsonResponse['documentFormat'],
          );

          this.documentReady = true;
          this.documentStatus = 'ready';
          this.isDownloading = false;
        } else {
          throw new Error('Document not available');
        }
      })
      .catch(error => {
        console.error('Download error:', error);
        this.isDownloading = false;
        this.downloadError = 'Eroare la descărcare. Încercați metoda alternativă.';
        this.snackBar.open(this.downloadError, 'OK', { duration: 3000 });
      });
  }

  retryDocumentCheck(): void {
    console.log('Retrying document check', {
      currentStatus: this.documentStatus,
      sessionId: this.sessionId
    });
    this.downloadError = null;
    this.documentStatus = 'pending';
    this.startDocumentCheck();
  }

  private handleSessionId(sessionId: string): void {
    this.sessionId = sessionId;
    this.isFreePurchase = sessionId.startsWith('free_');

    this.showSuccessMessage = true;
    this.documentStatus = 'pending';

    // Proceed with document check
    if (this.sessionId) {
      // Small delay before starting check to let UI render
      setTimeout(() => {
        this.startDocumentCheck();
      }, 500);
    } else {
      console.error('No session ID found in URL parameters');
      this.documentStatus = 'error';
      this.downloadError = 'Nu s-a găsit niciun ID de sesiune. Verificați URL-ul.';
      this.snackBar.open('Nu s-a găsit niciun ID de sesiune. Verificați URL-ul.', 'OK', { duration: 5000 });
    }
  }

  private startDocumentCheck(): void {
    this.checkingDocument = true;
    console.log('Starting document availability check for session ID:', this.sessionId);

    // Clear previous subscription if exists
    if (this.docCheckSubscription) {
      this.docCheckSubscription.unsubscribe();
    }

    // First immediate check
    this.checkDocumentAvailability();
  }

  private checkDocumentAvailability(): void {
    if (!this.sessionId || this.documentReady) {
      return;
    }

    this.documentStatus = 'checking';

    this.stripeService.getDocumentUrlBySessionId(this.sessionId)
      .pipe(
        catchError(error => {
          console.error('Error checking document availability:', error);
          // Return empty response to continue the stream
          return of(null);
        })
      )
      .subscribe({
        next: (response) => {
          console.log('Document URL retrieved:', response);

          let jsonResponse = JSON.parse(JSON.stringify(response));
          if (jsonResponse['message'] === 'OK') {
            this.downloadFile(
              Utilities.dataURItoBlob(jsonResponse['content']),
              jsonResponse['documentFormat'],
            );

            this.documentReady = true;
            this.documentStatus = 'ready';
          }
        },
        error: (error) => {
          console.error('Error in document check:', error);
          this.downloadError = 'Eroare la verificarea documentului. Vă rugăm încercați din nou.';
        }
      });
  }

  private downloadFile(blob: Blob, documentFormat: string) {
    console.log('Downloading file', { blobSize: blob.size, format: documentFormat });

    // Verificare modernă pentru compatibilitate browser
    if (window.navigator && (window.navigator as any).msSaveBlob) {
      // Pentru Internet Explorer și Edge
      (window.navigator as any).msSaveBlob(blob, `document.${documentFormat}`);
      return;
    }

    const dataURL = window.URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = dataURL;
    link.download = `document.${documentFormat}`;

    // Adăugare în DOM și declanșare descărcare
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    // Curățare URL obiect
    setTimeout(() => {
      window.URL.revokeObjectURL(dataURL);
    }, 100);
  }


  // private retrievePaymentInformation(): void {
  //   if (!this.sessionId) return;

  //   this.isLoadingInfo = true;

  //   this.stripeService.getPaymentBySessionId(this.sessionId)
  //     .pipe(
  //       catchError(error => {
  //         console.error('Error retrieving payment information:', error);
  //         // Try alternative method
  //         return this.stripeService.getDocumentUrlBySessionId(this.sessionId!).pipe(
  //           catchError(innerError => {
  //             console.error('Both payment retrieval methods failed:', innerError);
  //             return of(null);
  //           })
  //         );
  //       }),
  //       finalize(() => {
  //         this.isLoadingInfo = false;
  //       })
  //     )
  //     .subscribe(result => {
  //       if (result) {
  //         console.log('Payment information found:', result);
  //         this.payment = result;

  //         if (result.documentUrl) {
  //           this.documentStatus = 'ready';
  //           this.documentUrl = result.documentUrl;
  //           this.documentReady = true;
  //         } else if (result.documentStatus === 'generating') {
  //           this.documentStatus = 'generating';
  //         }
  //       } else {
  //         console.warn('No payment information found');
  //         // For free documents, create a placeholder payment object
  //         if (this.isFreePurchase) {
  //           const sessionIdParts = this.sessionId?.split('_') || [];
  //           if (sessionIdParts.length >= 3) {
  //             this.payment = {
  //               documentType: sessionIdParts[1]?.replace(/_/g, ' ').toUpperCase() || 'Document',
  //               documentFormat: sessionIdParts[2] || 'pdf',
  //               amount: 0,
  //               currency: 'RON',
  //               status: 'completed'
  //             };
  //           }
  //         }
  //       }
  //     });
  // }

} 